import type {
  NodeRenderer,
  Options,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Text } from '@contentful/rich-text-types';
import {
  renderRichText,
  type ContentfulRichTextGatsbyReference,
  type RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import { useCallback, useMemo, type FC } from 'react';

import { Anchor, Img } from 'src/components/common/atoms';
import { theme } from 'src/styles';
import { extractYouTubeVideoId } from 'src/utils';

// NOTE:
// referensesが必須になっているが、
// 実際はreferensesが無いこともあるので、
// 型操作を行う。
//
type RichTextDocumenType =
  RenderRichTextData<ContentfulRichTextGatsbyReference>;
type CustomRichTextDocumentType = {
  raw: RichTextDocumenType['raw'] | null;
  readonly references?: Queries.Maybe<
    ReadonlyArray<Queries.Maybe<ContentfulRichTextGatsbyReference>>
  >;
};
export type Props = {
  document: CustomRichTextDocumentType;
  isArchivePage?: boolean;
};

const isTextType = (arg: any): arg is Text => arg?.nodeType === 'text';

// https://www.contentful.com/developers/docs/tutorials/general/rich-text-and-gatsby/
// https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-source-contentful/CHANGELOG.md#400-next0-2020-11-09
const useOptions = (isArchivePage: Props['isArchivePage']) => {
  const linkResolver = useCallback<NodeRenderer>((node, children) => {
    const { content, data } = node;
    let isStrong = false;
    if (content?.[0] && isTextType(content[0])) {
      const hasUnderline = content[0].marks.find(
        (mark) => mark.type === 'underline'
      );
      const hasBold = content[0].marks.find((mark) => mark.type === 'bold');
      if (hasUnderline && hasBold) {
        isStrong = true;
      }
    }

    if (data.uri.includes('youtube.com')) {
      const videoId = extractYouTubeVideoId(data.uri);
      return (
        videoId && (
          <iframe
            className="youtube-embed-video"
            src={`https://youtube.com/embed/${videoId}?playsinline=1`}
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )
      );
    }

    return (
      <Anchor to={data.uri} className={isStrong ? 'strong' : ''}>
        {children}
      </Anchor>
    );
  }, []);

  const assetLinkResolver = useCallback<NodeRenderer>((node, children) => {
    const { target } = node.data;
    const href = target?.file?.url as string | undefined;
    if (!href) {
      return;
    }
    return (
      <a href={`https:${href}`} target="_blank" rel="noopenner noreferrer">
        {children}
      </a>
    );
  }, []);

  const renderAssetEmbedded = useCallback<NodeRenderer>((node, children) => {
    const image = node.data.target;
    const imageAlt = node.data.target.title;
    if (!image) {
      return null;
    }
    return (
      <Img
        image={image}
        alt={imageAlt ?? ''}
        sizes={`(min-width: ${
          theme.breakpoints.siteMaxWidth + 1
        }px) 565px, (min-width: ${
          theme.breakpoints.siteBreakpoint + 1
        }px) calc((100vw - ${
          theme.structure.site.margin.horizontal.sp * 2
        }) / 8 * 4 ), calc(100vw - ${
          theme.structure.site.margin.horizontal.sp * 2
        }px)`}
      />
    );
  }, []);

  const renderHeading2 = useCallback<NodeRenderer>(
    (node, children) => {
      return isArchivePage ? <h3>{children}</h3> : <h2>{children}</h2>;
    },
    [isArchivePage]
  );

  return useMemo<Options>(
    () => ({
      renderNode: {
        [INLINES.HYPERLINK]: linkResolver,
        [INLINES.ASSET_HYPERLINK]: assetLinkResolver,
        [BLOCKS.EMBEDDED_ASSET]: renderAssetEmbedded,
        [BLOCKS.HEADING_2]: renderHeading2,
      },
      // NOTE:
      // 改行を<br>に変換
      // https://github.com/contentful/rich-text/issues/96
      renderText: (text) =>
        text
          .split('\n')
          .flatMap((text, i) => [
            i > 0 && <br key={`line-break-${i}`} />,
            text,
          ]),
    }),
    [linkResolver, assetLinkResolver, renderHeading2]
  );
};

export const RichTextRenderer: FC<Props> = ({
  document,
  isArchivePage = false,
}) => {
  const options = useOptions(isArchivePage);
  if (!document?.raw) {
    return null;
  }
  return <>{renderRichText(document as RichTextDocumenType, options)}</>;
};

export default RichTextRenderer;
